/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.0.1
 */

import styled from 'styled-components';

import { colors, fonts } from '../../../../theme';
import { IAttrStyled } from '../../../../types/globalTypes';

const CalendarTableStyled = styled.div.attrs(({ className }: IAttrStyled) => ({
    className: `range-date-picker__calendar calendar-table ${className || ''}`,
}))<IAttrStyled>`
    background-color: #fff;

    & .range-date-picker__day-cell {
        width: 24px;
        height: 24px;
        margin: 0 auto;
        padding: 0 4px;
        border-radius: 50%;
        line-height: 25px;
    }

    & .range-date-picker__calendar {
        width: 100%;
        margin: 0;
        border-spacing: 0 8px;
        border-collapse: separate;
    }

    & .range-date-picker__day-cell:first-child {
        padding-left: 0;
    }

    & .range-date-picker__day-cell:last-child {
        padding-right: 0;
    }

    & .active {
        border: none;
        border-radius: 0;
        padding: 0 !important;
        outline: none;
        background: ${colors.backgroundColorHoverNormal};
    }

    & .range-date-picker__calendar-body {
        padding-top: 10px;
    }

    & .range-date-picker__calendar-body::before {
        content: "";
    }

    & .active.start-date.end-date {
        background: none !important;
    }

    .range-date-picker__calendar-left & .active.start-date {
        background: linear-gradient(to right, white 50% , ${colors.rangePickerBgHoverColor} 50%);
    }

    .range-date-picker__calendar-right & .active.end-date {
        background: linear-gradient(to left, white 50% , ${colors.rangePickerBgHoverColor} 50%);
    }

    .range-date-picker__calendar-left & .active.start-date > .range-date-picker__day-cell,
    .range-date-picker__calendar-right & .active.end-date > .range-date-picker__day-cell
     {
        border-color: transparent;
        background-color: ${colors.backgroundColorSuccess};
        color: ${colors.textColorWhite};
    }

    .range-date-picker__calendar-left & .active.start-date > .range-date-picker__day-cell:hover,
    .range-date-picker__calendar-right & .active.end-date > .range-date-picker__day-cell:hover
    {
        background-color: ${colors.backgroundColorHover};
    }

    &:not(.single-date-picker) .in-range {
        background-color: ${colors.rangePickerBgHoverColor};
        border-color: transparent;
        color: #000;
        border-radius: 0;
    }

    & :not(.disabled) .range-date-picker__day-cell:hover {
        border-radius: 100%;
        background-color: ${colors.backgroundColorHover};
        color: ${colors.textColorWhite};
    }
`;

const DateRangePickerStyled = styled.div.attrs(({ className }: IAttrStyled) => ({
    className: `${className} range-date-picker__wrapper daterangepicker ltr show-calendar`,
}))<IAttrStyled>`
    width: auto !important;
    display: flex !important;
    direction: ltr;
    text-align: left;

    &.daterangepicker {
        width: 472px;
        max-width: none;
        margin: 0 0 31.5px;
        color: inherit;
        background-color: #fff;
        font-size: 15px;
        line-height: 1em;
    };

    &.daterangepicker.show-calendar .drp-buttons {
        display: flex;
        justify-content: flex-end;
    }

    &.daterangepicker .week, .daterangepicker .week {
        font-size: 80%;
        color: #ccc;
    }

    & .day-of-week {
        padding: 0 4px;
    }

    &.daterangepicker .month {
        width: 100%;
        margin-bottom: 8px;
    }

    &.daterangepicker .disabled, .daterangepicker option.disabled {
        color: #999;
        cursor: not-allowed;
        text-decoration: line-through;
    }

    &.daterangepicker .ranges {
        float: none;
        text-align: left;
        margin: 0;
    }

    &.daterangepicker.show-calendar .ranges {
        margin-top: 8px;
    }

    &.daterangepicker .ranges ul {
        list-style: none;
        margin: 0 auto;
        padding: 0;
        width: 100%;
    }

    &.daterangepicker .ranges li {
        font-size: 12px;
        padding: 8px 12px;
        cursor: pointer;
    }
`;

const DrpCalendarStyled = styled.div.attrs({
    className: 'drp-calendar left',
})`
    &.drp-calendar {
        width: 224px;
    }

    &.drp-calendar.right {
        margin-left: 0;
    }

    &.drp-calendar.left {
        padding: 0;
    }

    &.drp-calendar.right {
        padding: 8px;
    }

    &.drp-calendar.left .range-date-picker__calendar-head-thead .calendar-table {
        padding-right: 8px;
    }

    &.drp-calendar.right .range-date-picker__calendar-head-thead .calendar-table {
        padding-right: 8px;
    }

    &.drp-calendar.left .calendar-table {
        border-right: none;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    &.drp-calendar.right .calendar-table {
        border-left: none;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
`;

const DrpCalendarLeftStyled = styled.div.attrs({
    'className': 'range-date-picker__calendar-left',
})`
    margin-right: 12px;
`;

const DrpCalendarRightStyled = styled.div.attrs({
    'className': 'range-date-picker__calendar-right',
})`
    margin-left: 12px;
`;

const InputTimeLabelStyled = styled.div`
    display: flex;
    justify-content: center;
    margin: 5.5px 0;
    ${fonts.textSmall};
`;

const InputTimeContainerStyled = styled.div`
    display: flex;
    justify-content: center;
    cursor: default;
    & .input-range {
        width: calc(100% - 25px);
        cursor: default;
    }
    & .input-range__label-container {
        display: none;
    }
    & .input-range__track--active {
        background: inherit;
    }
    & .input-range__slider {
        background: ${colors.backgroundColorSuccess};
        border-color: ${colors.borderColorSuccess};
    }
`;

const VerticalSeparateLineStyled = styled.div`
    border-right: 1px solid ${colors.borderColorNormal};
    opacity: .2;
`;

export {
    CalendarTableStyled,
    DateRangePickerStyled,
    DrpCalendarStyled,
    DrpCalendarLeftStyled,
    DrpCalendarRightStyled,
    InputTimeLabelStyled,
    InputTimeContainerStyled,
    VerticalSeparateLineStyled,
};
